import { Button, ConfirmHost, EmptyState, ErrorState, Toaster } from '@pos/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, RouterProvider, createHashRouter } from 'react-router';
import { HomePage } from '../features/home/HomePage';
import {
  BranchesPage,
  DevicesPage,
  EmployeesPage,
  ExpensesPage,
  MorePage,
} from '../features/more/MorePages';
import { NewProductPage, ProductPage, ProductsPage } from '../features/products/ProductPages';
import { ReportsPage } from '../features/reports/ReportsPage';
import { NotificationsPage } from '../features/more/NotificationsPage';
import { ProfilePage } from '../features/more/ProfilePage';
import { CustomerPage, CustomersPage } from '../features/customers/CustomerPages';
import { WriteOffPage } from '../features/stock/WriteOffPage';
import { SubscriptionPage } from '../features/more/SubscriptionPage';
import { SaleDetailPage, SalesPage } from '../features/sales/SalesPages';
import { ReceivePage, StockPage } from '../features/stock/StockPages';
import { restore, signOut, useAuth } from './auth';
import { Claim } from './Claim';
import { SignIn } from './SignIn';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true } },
});

const router = createHashRouter([
  { path: '/', element: <Navigate to="/home" replace /> },
  { path: '/home', element: <HomePage /> },
  { path: '/sales', element: <SalesPage /> },
  { path: '/sales/:id', element: <SaleDetailPage /> },
  { path: '/stock', element: <StockPage /> },
  { path: '/stock/receive', element: <ReceivePage /> },
  { path: '/stock/write-off', element: <WriteOffPage /> },
  { path: '/customers', element: <CustomersPage /> },
  { path: '/customers/:id', element: <CustomerPage /> },
  { path: '/products', element: <ProductsPage /> },
  { path: '/products/new', element: <NewProductPage /> },
  { path: '/products/:id', element: <ProductPage /> },
  { path: '/more', element: <MorePage /> },
  { path: '/more/reports', element: <ReportsPage /> },
  { path: '/more/expenses', element: <ExpensesPage /> },
  { path: '/more/employees', element: <EmployeesPage /> },
  { path: '/more/branches', element: <BranchesPage /> },
  { path: '/more/devices', element: <DevicesPage /> },
  { path: '/more/subscription', element: <SubscriptionPage /> },
  { path: '/more/notifications', element: <NotificationsPage /> },
  { path: '/more/profile', element: <ProfilePage /> },
  { path: '*', element: <Navigate to="/home" replace /> },
]);

const claimToken = (hash: string) => /^#\/claim\/([A-Za-z0-9_-]{20,200})$/.exec(hash)?.[1] ?? null;

function Gate() {
  const { status, me, businessId } = useAuth();
  // The landing page's «Попробовать» links straight to sign-up: #/signup.
  const [newShop] = useState(() => window.location.hash.startsWith('#/signup'));
  // A one-time invitation link: #/claim/<token>.
  const [claim, setClaim] = useState(() => claimToken(window.location.hash));
  useEffect(() => {
    void restore();
  }, []);
  if (status === 'loading') return <div className="min-h-dvh floor" aria-busy />;
  if (claim)
    return (
      <Claim
        token={claim}
        onDone={() => {
          window.location.hash = '#/home';
          setClaim(null);
        }}
      />
    );
  if (status === 'signed-out') return <SignIn newShop={newShop} />;
  if (!me)
    return (
      <div className="flex min-h-dvh items-center justify-center floor px-4">
        <ErrorState
          title="Нет связи с сервером"
          description="Проверьте интернет и повторите."
          onRetry={() => void restore()}
        />
      </div>
    );
  const membership = me.memberships.find((m) => m.businessId === businessId);
  if (!membership)
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 floor px-4">
        <EmptyState
          title="Нет доступных бизнесов"
          description="Ваш доступ отключён или ещё не выдан."
        />
        <Button onClick={() => void signOut()}>Выйти</Button>
      </div>
    );
  // The owner app is for people who see the numbers; the server enforces this per request too.
  if (!membership.permissions.includes('reports.view'))
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 floor px-4">
        <EmptyState
          title="Нет доступа"
          description={`Роль «${membership.roleName}» не видит отчёты. Приложение владельца для владельца и управляющих.`}
        />
        <Button onClick={() => void signOut()}>Выйти</Button>
      </div>
    );
  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Gate />
      <Toaster position="top-center" />
      <ConfirmHost />
    </QueryClientProvider>
  );
}
